import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Phone,
  Calendar,
  CircleDollarSign,
  CheckCircle2,
  AlertCircle,
  Undo2,
  DollarSign,
  MessageCircle,
  Copy,
  ExternalLink,
} from "lucide-react";
import { brl, dt, statusMeta, methodLabel } from "@/lib/format";
import { toast } from "sonner";

export default function ClientDetailSheet({ client, onOpenChange, onPay, onUndo, onRemind }) {
  if (!client) return null;
  const s = statusMeta[client.status] || statusMeta.pendente;
  const waPhone = (client.phone || "").replace(/\D/g, "");

  const copyReceiptLink = async (token) => {
    const url = `${window.location.origin}/r/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link do recibo copiado");
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  return (
    <Sheet open={!!client} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="bg-[#0B132B] border-slate-800 text-slate-100 w-full sm:max-w-lg overflow-y-auto"
        data-testid="client-detail-sheet"
      >
        <SheetHeader className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <SheetTitle className="text-white text-xl" style={{ fontFamily: "Outfit" }}>
                {client.name}
              </SheetTitle>
              <SheetDescription className="text-slate-400 text-xs">
                Cliente desde {dt(client.created_at)}
              </SheetDescription>
            </div>
            <Badge variant="outline" className={`${s.bg} ${s.color} ${s.border}`}>
              <span className={`status-dot mr-2 ${s.dot}`} />
              {s.label}
            </Badge>
          </div>

          {client.phone && (
            <div className="flex items-center gap-2 text-sm text-slate-300">
              <Phone className="w-4 h-4 text-blue-400" />
              {client.phone}
              <div className="ml-auto flex items-center gap-2">
                {waPhone && (
                  <a
                    href={`https://wa.me/${waPhone}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300"
                    data-testid="wa-link"
                  >
                    <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                  </a>
                )}
                {client.status !== "pago" && onRemind && (
                  <button
                    onClick={onRemind}
                    className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
                    data-testid="detail-remind"
                  >
                    <MessageCircle className="w-3.5 h-3.5" /> Enviar lembrete
                  </button>
                )}
              </div>
            </div>
          )}
        </SheetHeader>

        <div className="grid grid-cols-3 gap-3 my-6">
          <div className="glass-card p-3">
            <div className="text-[10px] uppercase text-slate-500 mb-1 tracking-widest">
              Emprestado
            </div>
            <div className="metric-value text-sm text-white">{brl(client.loan_amount)}</div>
          </div>
          <div className="glass-card p-3">
            <div className="text-[10px] uppercase text-slate-500 mb-1 tracking-widest">Saldo devedor</div>
            <div className="metric-value text-sm text-amber-400">{brl(client.current_principal ?? client.balance)}</div>
          </div>
          <div className="glass-card p-3">
            <div className="text-[10px] uppercase text-slate-500 mb-1 tracking-widest">Próx. juros</div>
            <div className="metric-value text-sm text-blue-400">{brl(client.next_interest ?? 0)}</div>
          </div>
        </div>

        <div className="glass-card p-4 mb-4 text-sm space-y-2">
          <div className="flex items-center justify-between text-slate-300">
            <span className="text-slate-500 flex items-center gap-2">
              <Calendar className="w-4 h-4" /> Data do empréstimo
            </span>
            <span className="font-mono">{dt(client.loan_date)}</span>
          </div>
          <div className="flex items-center justify-between text-slate-300">
            <span className="text-slate-500 flex items-center gap-2">
              <CircleDollarSign className="w-4 h-4" /> Cobrança
            </span>
            <span>
              {client.collection_method === "a_vista"
                ? "À Vista"
                : `${client.installments_count}x ${client.collection_frequency}`}
            </span>
          </div>
          <div className="flex items-center justify-between text-slate-300">
            <span className="text-slate-500">Juros</span>
            <span className="font-mono">{client.interest_rate}%</span>
          </div>
          {client.notes && (
            <div className="pt-2 border-t border-slate-800/70 text-slate-400 text-xs italic">
              {client.notes}
            </div>
          )}
        </div>

        <div>
          <h3 className="text-sm font-semibold text-white mb-3 tracking-tight" style={{ fontFamily: "Outfit" }}>
            Parcelas
          </h3>
          <div className="space-y-2">
            {client.installments.map((it) => {
              const interestExpected = Number(it.amount ?? it.interest_amount ?? 0);
              const paidAmt = Number(it.paid_amount ?? 0);
              const isPartial = it.paid && paidAmt < interestExpected - 0.01;
              return (
                <div
                  key={it.number}
                  className={`p-3 rounded-lg border ${
                    it.paid
                      ? isPartial
                        ? "bg-amber-500/5 border-amber-500/30"
                        : "bg-emerald-500/5 border-emerald-500/25"
                      : "bg-slate-900/40 border-slate-800"
                  }`}
                  data-testid={`installment-${it.number}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-slate-200 font-medium">
                        Parcela {it.number} · Previsto {brl(interestExpected)}
                      </div>
                      {it.paid ? (
                        <div className="text-xs font-mono">
                          <span className={isPartial ? "text-amber-300" : "text-emerald-300"}>
                            {isPartial ? "Pago parcial · rolou p/ próximo mês" : "Pago"}
                          </span>
                          <span className="text-slate-500">
                            {" · "}
                            {brl(paidAmt)} em {dt(it.paid_at)}
                            {it.payment_method ? ` · ${methodLabel[it.payment_method]}` : ""}
                          </span>
                          {Number(it.principal_amount ?? 0) > 0 && (
                            <span className="text-blue-300">
                              {" · abateu "}
                              {brl(it.principal_amount)}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="text-xs text-slate-500 font-mono">
                          Vence em {dt(it.due_date)}
                        </div>
                      )}
                    </div>
                    {it.paid ? (
                      <div className="flex items-center gap-1 shrink-0">
                        {isPartial ? (
                          <AlertCircle className="w-5 h-5 text-amber-400" />
                        ) : (
                          <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                        )}
                        {it.receipt_token && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-slate-400 hover:text-blue-300 h-8"
                              onClick={() => copyReceiptLink(it.receipt_token)}
                              data-testid={`copy-receipt-${it.number}`}
                              title="Copiar link do recibo"
                            >
                              <Copy className="w-3.5 h-3.5" />
                            </Button>
                            <a
                              href={`/r/${it.receipt_token}`}
                              target="_blank"
                              rel="noreferrer"
                              className="p-1.5 text-slate-400 hover:text-blue-300"
                              data-testid={`open-receipt-${it.number}`}
                              title="Abrir recibo"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          </>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-slate-400 hover:text-rose-300 h-8"
                          onClick={() => onUndo?.(it)}
                          data-testid={`undo-${it.number}`}
                          title="Desfazer pagamento"
                        >
                          <Undo2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-500 text-white h-8 shrink-0"
                        onClick={() => onPay?.(it)}
                        data-testid={`pay-inst-${it.number}`}
                      >
                        <DollarSign className="w-3.5 h-3.5 mr-1" /> Pagar
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
